import { Button, Container, Row, Col, Card, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import ResponsiveMultiSelect from '../Profile/ResponsiveMultiSelect';
import countries from '../../data/countries';
import icValidationError from '../../assets/img/ic-danger.svg';
import icInfo from '../../assets/img/ic-info.svg';
import studyDirectionsData from '../../data/studyDirections.json';

const RegisterTalentStep3Info = ({ values, setters, errors, onNext, onBack, showErrorMessage, setShowErrorMessage, isMobile = false, missingFields = null }) => {
    const showField = field => !missingFields || missingFields.includes(field);
    const navigate = useNavigate();

    const {
        birthDate,
        citizenship1,
        citizenship2,
        country,
        city,
        futureEducationalInstitution,
        plannedStudyDirection,
        plannedSpecialization,
        futureDegree,
        plannedGraduationYear,
        plannedGraduationQuarter,
        currentSemester,
    } = values;

    const {
        setBirthDate,
        setCitizenship1,
        setCitizenship2,
        setCountry,
        setCity,
        setFutureEducationalInstitution,
        setPlannedStudyDirection,
        setPlannedSpecialization,
        setFutureDegree,
        setPlannedGraduationYear,
        setPlannedGraduationQuarter,
        setCurrentSemester,
    } = setters;

    const uniqueCountries = useMemo(
        () => Array.from(new Map(countries.map(c => [c.id, c])).values()),
        []
    );

    const countryOptions = useMemo(
        () => uniqueCountries.map(c => ({ value: String(c.id), label: c.name })),
        [uniqueCountries]
    );

    const countryIdToName = useMemo(
        () => Object.fromEntries(uniqueCountries.map(c => [String(c.id), c.name])),
        [uniqueCountries]
    );

    // support old saved values (e.g. "Germany") by trying to map name -> id
    const nameToCountryId = useMemo(() => {
        const map = {};
        uniqueCountries.forEach(c => {
            map[String(c.name || '').trim().toLowerCase()] = String(c.id);
        });
        return map;
    }, [uniqueCountries]);

    const resolveCountryId = (rawValue) => {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';
        if (countryIdToName[raw]) return raw;                 // already ISO2
        return nameToCountryId[raw.toLowerCase()] || raw;     // name -> ISO2, else keep raw
    };

    // Residence country select value (your existing one)
    const resolvedCountryId = useMemo(() => resolveCountryId(country), [country]);

    // Citizenship select values
    const resolvedCitizenship1Id = useMemo(() => resolveCountryId(citizenship1), [citizenship1]);
    const resolvedCitizenship2Id = useMemo(() => resolveCountryId(citizenship2), [citizenship2]);

    // Prevent picking same citizenship twice (for dropdown #2)
    const citizenship2Options = useMemo(() => {
        const c1 = String(resolvedCitizenship1Id || '').trim();
        return countryOptions.filter(opt => String(opt.value) !== c1);
    }, [countryOptions, resolvedCitizenship1Id]);

    // Grouped study directions (native <select> with <optgroup>)
    const studyDirectionOptionsGrouped = useMemo(
        () =>
            Object.entries(studyDirectionsData).map(([category, directions]) => ({
                label: category,
                options: (directions || []).map((d) => ({
                    label: d,
                    value: d,
                })),
            })),
        []
    );

    // Flatten list for quick checks (legacy-safe)
    const allStudyDirectionValues = useMemo(
        () => new Set(studyDirectionOptionsGrouped.flatMap(g => g.options.map(o => String(o.value)))),
        [studyDirectionOptionsGrouped]
    );


    const FieldError = ({ message }) => {
        if (!message) return null;
        return (
            <div className="custom-error-icon-wrapper">
                <img
                    src={icValidationError}
                    alt={message}
                    className="custom-error-icon"
                />
                <div className="custom-tooltip">{message}</div>
            </div>
        );
    };

    const FieldInfo = ({ message }) => {
        if (!message) return null;
        return (
            <div className="custom-info-icon-wrapper">
                <img
                    src={icInfo}
                    alt={message}
                    className="custom-info-icon"
                />
                <div className="custom-tooltip custom-tooltip-info">{message}</div>
            </div>
        );
    };

    // age calc helpers
    const calcAge = (isoDate) => {
        const s = String(isoDate || '').trim();
        if (!s) return null;

        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return null;

        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const m = today.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
        return age;
    };

    const birthAge = useMemo(() => calcAge(birthDate), [birthDate]);

    const birthDateWarning = useMemo(() => {
        if (birthAge === null) return '';
        if (birthAge < 16) return 'You appear to be under 16. You can continue, but some opportunities may be limited.';
        if (birthAge > 35) return 'You appear to be over 35. You can continue, but some programs may have age limits.';
        return '';
    }, [birthAge]);

    return (
        <main className="main-content flex-grow-1 ms-0 register-page">
            <Container>
                <Row>
                    <Col xl={6} className="offset-xl-4 pt-5 col-xl-4">
                        <Card className="mb-4">
                            <Card.Body>
                                <Form onSubmit={onNext} noValidate>
                                    <div className="mb-4">
                                        <h2 className="mb-2">{missingFields ? 'Finish your registration details' : 'Your details'}</h2>
                                        <p className="mb-0">{missingFields
                                            ? 'Your profile is complete. Only these remaining registration details are needed; your saved information is preserved.'
                                            : 'This helps us match you with the right opportunities.'}</p>
                                    </div>

                                    {showErrorMessage && (!isMobile || errors.step3 !== 'Please fix the highlighted fields.') && (
                                        <Alert
                                            variant="danger"
                                            onClose={() => setShowErrorMessage(false)}
                                            dismissible
                                            className="mb-3"
                                        >
                                            <p className="mb-0">
                                                <strong>Error</strong> – {isMobile && errors.step3
                                                    ? errors.step3
                                                    : 'There are items that require your attention.'}
                                            </p>
                                        </Alert>
                                    )}

                                    <Row className="register-field-row" hidden={!['birthDate', 'citizenships'].some(showField)}>
                                        <Col md={6} className="register-edit-field" hidden={!showField('birthDate')}>
                                            <Form.Group className="validated" controlId="birthDate" data-register-field="birthDate">
                                                <Form.Label>Birth date <span>*</span></Form.Label>

                                                <div
                                                    onClick={() => document.getElementById('birthDateStep3')?.showPicker?.()}
                                                    style={{ position: 'relative', cursor: 'pointer' }}
                                                >
                                                    <Form.Control
                                                        id="birthDateStep3"
                                                        type="date"
                                                        value={birthDate}
                                                        onChange={(e) => setBirthDate(e.target.value)}
                                                        isInvalid={!!errors?.birthDate}
                                                        required
                                                        className="custom-date-input"
                                                    />

                                                    {/* ✅ error tooltip icon */}
                                                    <FieldError message={errors?.birthDate} />

                                                    {/* ✅ warning tooltip icon (only if no error) */}
                                                    {!errors?.birthDate && (
                                                        <FieldInfo message={birthDateWarning} />
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} className="register-edit-field" hidden={!showField('citizenships')}>
                                            <Form.Group className="validated" controlId="citizenship1" data-register-field="citizenship1">
                                                <Form.Label>Citizenship <span>*</span></Form.Label>
                                                <Form.Select
                                                    value={resolvedCitizenship1Id || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setCitizenship1(val);

                                                        // if user picked same as #2, clear #2
                                                        if (String(val) && String(val) === String(resolvedCitizenship2Id)) {
                                                            setCitizenship2('');
                                                        }
                                                    }}
                                                    isInvalid={!!errors?.citizenship1}
                                                >
                                                    <option value="">Select citizenship</option>
                                                    {countryOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <FieldError message={errors?.citizenship1} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="register-field-row mt-3" hidden={!['citizenships', 'country'].some(showField)}>
                                        <Col md={6} className="register-edit-field" hidden={!showField('citizenships')}>
                                            <Form.Group className="validated" controlId="citizenship2">
                                                <Form.Label>Citizenship #2</Form.Label>
                                                <Form.Select
                                                    value={resolvedCitizenship2Id || ''}
                                                    onChange={(e) => setCitizenship2(e.target.value)}
                                                >
                                                    <option value="">None</option>
                                                    {citizenship2Options.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="register-edit-field" hidden={!showField('country')}>
                                            <Form.Group className="validated" controlId="country" data-register-field="country">
                                                <Form.Label>Country of residence <span>*</span></Form.Label>
                                                <Form.Select
                                                    value={resolvedCountryId || ''}
                                                    onChange={(e) => setCountry(e.target.value)}
                                                    isInvalid={!!errors?.country}
                                                    required
                                                >
                                                    <option value="">Select country</option>
                                                    {countryOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <FieldError message={errors?.country} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="register-field-row mt-3" hidden={!['city', 'futureEducationalInstitution'].some(showField)}>
                                        <Col md={6} className="register-edit-field" hidden={!showField('city')}>
                                            <Form.Group className="validated" controlId="city" data-register-field="city">
                                                <Form.Label>City of residence <span>*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={city}
                                                    onChange={(e) => setCity(e.target.value)}
                                                    isInvalid={!!errors?.city}
                                                    required
                                                />
                                                <FieldError message={errors?.city} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="register-edit-field" hidden={!showField('futureEducationalInstitution')}>
                                            <Form.Group className="validated" controlId="futureEducationalInstitution" data-register-field="futureEducationalInstitution">
                                                <Form.Label>School / university <span>*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={futureEducationalInstitution}
                                                    onChange={(e) => setFutureEducationalInstitution(e.target.value)}
                                                    isInvalid={!!errors?.futureEducationalInstitution}
                                                    required
                                                />
                                                <FieldError message={errors?.futureEducationalInstitution} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="register-field-row mt-3" hidden={!['plannedStudyDirection', 'plannedSpecialization'].some(showField)}>
                                        <Col md={6} className="register-edit-field" hidden={!showField('plannedStudyDirection')}>
                                            <Form.Group className="validated" controlId="plannedStudyDirection" data-register-field="plannedStudyDirection">
                                                <Form.Label>Planned study direction <span>*</span></Form.Label>
                                                <ResponsiveMultiSelect
                                                    label="Planned study direction"
                                                    name="plannedStudyDirection"
                                                    inputId="plannedStudyDirection"
                                                    classNamePrefix="react-select"
                                                    className={`react-select ${errors?.plannedStudyDirection ? 'is-invalid' : ''}`}
                                                    placeholder="Select..."
                                                    searchPlaceholder="Search study directions..."
                                                    value={
                                                        (Array.isArray(plannedStudyDirection) ? plannedStudyDirection : [])
                                                            .filter(Boolean)
                                                            .map((val) => ({ label: val, value: val }))
                                                    }
                                                    onChange={(selected) =>
                                                        setPlannedStudyDirection((selected || []).map((opt) => opt.value))
                                                    }
                                                    options={studyDirectionOptionsGrouped}
                                                    required
                                                />

                                                <FieldError message={errors?.plannedStudyDirection} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="register-edit-field" hidden={!showField('plannedSpecialization')}>
                                            <Form.Group className="validated" controlId="plannedSpecialization">
                                                <Form.Label>Planned specialization</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={plannedSpecialization}
                                                    onChange={(e) => setPlannedSpecialization(e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="register-field-row mt-3" hidden={!['futureDegree'].some(showField)}>
                                        <Col md={6} className="register-edit-field" hidden={!showField('futureDegree')}>
                                            <Form.Group className="validated" controlId="futureDegree" data-register-field="futureDegree">
                                                <Form.Label>Planned graduation level <span>*</span></Form.Label>
                                                <Form.Select
                                                    value={futureDegree}
                                                    onChange={(e) => setFutureDegree(e.target.value)}
                                                    isInvalid={!!errors?.futureDegree}
                                                >
                                                    <option value="">Select degree...</option>
                                                    <option value="Bachelor's degree">Bachelor's degree</option>
                                                    <option value="Master's degree">Master's degree</option>
                                                    <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                                                    <option disabled>---------------------------</option>
                                                    <option value="No degree">No degree</option>
                                                    <option value="High school diploma">High school diploma</option>
                                                    <option value="Vocational qualification">Vocational qualification</option>
                                                    <option value="Associate degree">Associate degree</option>
                                                    <option value="Professional degree">Professional degree</option>
                                                    <option value="Postdoctoral qualification">Postdoctoral qualification</option>
                                                    <option value="Other">Other</option>
                                                </Form.Select>
                                                <FieldError message={errors?.futureDegree} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="register-field-row mt-3" hidden={!['plannedGraduationYear', 'plannedGraduationQuarter', 'currentSemester'].some(showField)}>
                                        <Col md={3} className="register-edit-field" hidden={!showField('plannedGraduationYear')}>
                                            <Form.Group className="validated" controlId="plannedGraduationYear" data-register-field="plannedGraduationYear">
                                                <Form.Label>Expected year <span>*</span></Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={plannedGraduationYear}
                                                    onChange={(e) => setPlannedGraduationYear(e.target.value)}
                                                    isInvalid={!!errors?.plannedGraduationYear}
                                                    placeholder="e.g. 2027"
                                                    required
                                                />
                                                <FieldError message={errors?.plannedGraduationYear} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3} className="register-edit-field" hidden={!showField('plannedGraduationQuarter')}>
                                            <Form.Group className="validated" controlId="plannedGraduationQuarter">
                                                <Form.Label>Expected quarter</Form.Label>
                                                <Form.Select
                                                    value={plannedGraduationQuarter}
                                                    onChange={(e) => setPlannedGraduationQuarter(e.target.value)}
                                                >
                                                    <option value="Q1">Spring</option>
                                                    <option value="Q2">Summer</option>
                                                    <option value="Q3">Autumn</option>
                                                    <option value="Q4">Winter</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        
                                        <Col md={6} className="register-edit-field register-edit-field--last" hidden={!showField('currentSemester')}>
                                            <Form.Group className="validated" controlId="currentSemester" data-register-field="currentSemester">
                                                <Form.Label>Current semester <span>*</span></Form.Label>

                                                <Form.Select
                                                    value={currentSemester || ''}
                                                    onChange={(e) => setCurrentSemester(e.target.value)}
                                                    isInvalid={!!errors?.currentSemester}
                                                    required
                                                >
                                                    <option value="">Select semester</option>
                                                    {[...Array(12)].map((_, i) => {
                                                        const num = i + 1;
                                                        return (
                                                            <option key={num} value={num}>
                                                                Semester {num}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Select>

                                                <FieldError message={errors?.currentSemester} />
                                            </Form.Group>
                                        </Col>
                                        
                                    </Row>

                                    <Button type="submit" variant="primary w-100 justify-content-center mt-4">
                                        Continue
                                    </Button>

                                    <Row className="mt-3">
                                        <Col xs={12} className="text-center">
                                            <Button
                                                type="button"
                                                variant="outline-secondary w-100 justify-content-center"
                                                onClick={typeof onBack === 'function' ? onBack : () => navigate('/')}
                                            >
                                                Maybe later
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </main>
    );
};

export default RegisterTalentStep3Info;
